import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  excuteAutoCommands,
  getAutomationCommands,
  resetExcuteAutoCommand,
} from "../redux/otherActions/autoCommandSlice";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import TableSkeleton from "../components/common/tableSkeleton";
import ErrorDisplay from "../components/common/error";
import ConfirmDialog from "../components/common/confirmDialog";
import { useTranslation } from "react-i18next";
import useBranchGuard from "../hooks/useBranchGuard";
import { humanizeError } from "../utils/errorText";

const AutomationCommand = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const branchUrl = useParams().id;
  useBranchGuard();

  const { loading, success, error } = useSelector(
    (state) => state.autoCommand.excuteAutoCommand
  );
  const {
    loading: getLoading,
    success: getSuccess,
    error: getError,
    data,
  } = useSelector((state) => state.autoCommand.getAutoCommand);
  const [msg, setMsg] = useState(null);
  const [pendingCmd, setPendingCmd] = useState(null);
  const [loadingToastId, setLoadingToastId] = useState(null);

  useEffect(() => {
    if (loading && loadingToastId === null) {
      setLoadingToastId(
        toast.loading(t("common.loading"), { position: "top-right" })
      );
      return;
    }
    if (!loading && loadingToastId !== null) {
      toast.dismiss(loadingToastId);
      setLoadingToastId(null);
    }
    if (success) {
      toast.success(t("actions.commandMsg", { msg }), {
        position: "top-right",
      });
      dispatch(resetExcuteAutoCommand());
    }
    if (error) {
      toast.error(humanizeError(error, t), { position: "top-right" });
      dispatch(resetExcuteAutoCommand());
    }
  }, [loading, success, error, dispatch, msg, t, loadingToastId]);

  useEffect(() => {
    if (!data?.[branchUrl]) {
      dispatch(getAutomationCommands({ branchUrl }));
    }
  }, [branchUrl, data, dispatch]);

  const confirmCommand = () => {
    const command = pendingCmd;
    setPendingCmd(null);
    setMsg(command.displayName);
    dispatch(excuteAutoCommands({ branchUrl, str: command.name }));
  };

  const commands = data?.[branchUrl];

  return (
    <div className={`mb-6 sm:mb-4 ${className || ""}`}>
      <ConfirmDialog
        open={Boolean(pendingCmd)}
        onClose={() => setPendingCmd(null)}
        onConfirm={confirmCommand}
        title={t("confirm.commandTitle", { name: pendingCmd?.displayName })}
        message={t("confirm.commandMsg")}
      />

      {getLoading ? (
        <TableSkeleton />
      ) : getSuccess && Array.isArray(commands) ? (
        <div className="w-full text-left text-[--gr-5]">
          <h2 className="px-6 py-1 font-medium text-lg max-sm:text-base bg-[--gr-4] text-slate-300">
            {t("actions.command")}
          </h2>
          <div className="gap-1 sm:gap-2 flex flex-col mt-2 font-medium w-full text-base max-sm:text-sm">
            {commands.map((entity, index) => (
              <button
                type="button"
                key={entity.name || index}
                onClick={() => setPendingCmd(entity)}
                disabled={loading}
                className="w-full text-left px-6 py-4 bg-[--primary] hover:bg-[--primary-hover] rounded-sm text-white disabled:opacity-50"
              >
                {entity.displayName}
              </button>
            ))}
          </div>
        </div>
      ) : getError ? (
        <ErrorDisplay
          error={getError}
          onRetry={() => dispatch(getAutomationCommands({ branchUrl }))}
        />
      ) : null}
    </div>
  );
};

export default AutomationCommand;
